<script setup lang="ts">
import { computed } from 'vue';
import { $t } from '@teachino/shared-i18n';
import type { StepProgressBarProps } from './step-progress-bar';

const props = withDefaults(defineProps<StepProgressBarProps>(), {
  variant: 'neutral',
  animateCurrentStep: false,
  ariaValueText: undefined,
});

const isActiveSegment = (index: number): boolean =>
  props.animateCurrentStep && index === props.currentStep;

const isCompletedSegment = (index: number): boolean =>
  index < props.currentStep ||
  (!props.animateCurrentStep && index === props.currentStep);

const completedClass = computed(() =>
  props.variant === 'primary' ? 'bg-primary-500' : 'bg-slate-900',
);

const getSegmentClass = (index: number): string => {
  if (isCompletedSegment(index)) {
    return completedClass.value;
  }
  if (isActiveSegment(index)) {
    return props.variant === 'primary' ? 'bg-primary-100' : 'bg-slate-300';
  }
  return 'bg-slate-200';
};
</script>

<template>
  <div
    class="flex w-full gap-2"
    :aria-label="$t('feature_course.onboarding.progress.aria_label')"
    role="progressbar"
    :aria-valuemin="1"
    :aria-valuemax="stepCount"
    :aria-valuenow="currentStep"
    :aria-valuetext="ariaValueText"
  >
    <div v-for="index in stepCount" :key="index" class="w-full">
      <div
        class="h-1.5 w-full overflow-hidden rounded-full transition-colors duration-200"
        :class="getSegmentClass(index)"
      >
        <div
          v-if="isActiveSegment(index)"
          class="step-progress-bar__sweep h-full w-1/2 rounded-full"
          :class="completedClass"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes step-progress-bar-sweep {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(200%);
  }
}

.step-progress-bar__sweep {
  animation: step-progress-bar-sweep 1400ms ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .step-progress-bar__sweep {
    animation: none;
  }
}
</style>
